import React, { useMemo, useState } from "react"

const emptyForm = { name: "", description: "", department_id: "" }

const ColumnTemplatesPanel = ({
  isAdmin,
  columns,
  visibleCols,
  colOrder,
  templates,
  departments,
  appliedTemplateId,
  onApplyTemplate,
  onCreateTemplate,
  onUpdateTemplate,
  onDeleteTemplate,
}) => {
  const [deptFilter, setDeptFilter] = useState("all")
  const [editing, setEditing] = useState(null) // null | "new" | template.id
  const [form, setForm] = useState(emptyForm)
  // Колонки, которые войдут в шаблон. Отдельное состояние от вкладки "Колонки" —
  // админ явно решает, что сохранять, а не полагается на текущее скрытое состояние.
  const [selectedCols, setSelectedCols] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  // Сравнение через String() — id шаблона может прийти как number (из JSON API)
  // или как string (например, если где-то по пути прошёл через params/URL),
  // так что сравниваем по строковому представлению, чтобы не разъезжалось.
  const isTemplateApplied = (templateId) => {
    if (appliedTemplateId == null) return false
    return String(appliedTemplateId) === String(templateId)
  }

  const filtered = useMemo(() => {
    if (deptFilter === "all") return templates
    return templates.filter((t) => String(t.department_id) === String(deptFilter))
  }, [templates, deptFilter])

  // Порядок для чек-листа: используем colOrder (текущий порядок колонок в таблице),
  // чтобы список выглядел так же, как на вкладке "Колонки".
  const orderedColumns = useMemo(() => {
    return colOrder.map((id) => columns.find((c) => c.id === id)).filter(Boolean)
  }, [colOrder, columns])

  const startCreate = () => {
    setForm(emptyForm)
    // По умолчанию — то, что сейчас видно в таблице, но это лишь стартовая точка,
    // админ может тут же поменять набор.
    setSelectedCols(visibleCols)
    setEditing("new")
    setError(null)
  }

  const startEdit = (t) => {
    setForm({
      name: t.name,
      description: t.description ?? "",
      department_id: t.department_id ?? "",
    })
    setSelectedCols(t.visible ?? [])
    setEditing(t.id)
    setError(null)
  }

  const cancelEdit = () => {
    setEditing(null)
    setForm(emptyForm)
    setSelectedCols([])
    setError(null)
  }

  const toggleCol = (id, fixed) => {
    if (fixed) return
    setSelectedCols((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]))
  }

  const selectAll = () => setSelectedCols(orderedColumns.map((c) => c.id))
  const selectNone = () => setSelectedCols(orderedColumns.filter((c) => c.fixed).map((c) => c.id))

  const save = async () => {
    if (!form.name.trim()) {
      setError("Укажите название шаблона")
      return
    }
    if (selectedCols.length === 0) {
      setError("Выберите хотя бы одну колонку")
      return
    }
    setSaving(true)
    setError(null)
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        department_id: form.department_id || null, // "" → null = без департамента
        visible: selectedCols,
        order: colOrder,
      }
      if (editing === "new") {
        await onCreateTemplate(payload)
      } else {
        await onUpdateTemplate(editing, payload)
      }
      cancelEdit()
    } catch (e) {
      setError("Не удалось сохранить шаблон")
    } finally {
      setSaving(false)
    }
  }

  const remove = async (id) => {
    if (!window.confirm("Удалить шаблон?")) return
    try {
      await onDeleteTemplate(id)
    } catch (e) {
      setError("Не удалось удалить шаблон")
    }
  }

  return (
    <div className="space-y-3">
      {/* Фильтр по департаменту — просто способ найти нужный шаблон,
          не привязан к текущему пользователю */}
      {departments.length > 0 && (
        <select
          value={deptFilter}
          onChange={(e) => setDeptFilter(e.target.value)}
          className="w-full text-xs border border-gray-200 rounded-xl px-3 py-2 text-gray-600"
        >
          <option value="all">Все департаменты</option>
          {departments.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
      )}

      {error && <div className="text-xs text-red-500 px-1">{error}</div>}

      <div className="space-y-1">
        {filtered.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-6">Нет сохранённых шаблонов</p>
        )}

        {filtered.map((t) => {
          const isApplied = isTemplateApplied(t.id)
          return (
            <div
              key={t.id}
              className={`rounded-xl border px-4 py-3 transition-colors
                ${isApplied ? "border-blue-200 bg-blue-50" : "border-gray-100 hover:bg-gray-50"}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{t.name}</p>
                  {t.department_name ? (
                    <p className="text-[11px] text-gray-400">{t.department_name}</p>
                  ) : (
                    <p className="text-[11px] text-gray-300">Без департамента</p>
                  )}
                  {t.description && (
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{t.description}</p>
                  )}
                  {Array.isArray(t.visible) && (
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      {t.visible.length} {pluralizeColumns(t.visible.length)}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {isApplied ? (
                    <span className="text-[11px] text-blue-600 font-medium px-2">Применён</span>
                  ) : (
                    <button
                      onClick={() => onApplyTemplate(t)}
                      className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                    >
                      Применить
                    </button>
                  )}
                  {isAdmin && (
                    <>
                      <button
                        onClick={() => startEdit(t)}
                        className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                        title="Редактировать"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => remove(t.id)}
                        className="p-1.5 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500"
                        title="Удалить"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {isAdmin && editing === null && (
        <button
          onClick={startCreate}
          className="w-full text-xs font-medium text-blue-600 border border-dashed border-blue-200 rounded-xl py-2.5 hover:bg-blue-50 transition-colors"
        >
          + Создать шаблон
        </button>
      )}

      {isAdmin && editing !== null && (
        <div className="border border-gray-200 rounded-xl p-3 space-y-3">
          <input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Название шаблона"
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
          />
          <textarea
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Описание (необязательно)"
            rows={2}
            className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 resize-none"
          />

          {departments.length > 0 && (
            <select
              value={form.department_id}
              onChange={(e) => setForm((f) => ({ ...f, department_id: e.target.value }))}
              className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 text-gray-600"
            >
              <option value="">Без департамента</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          )}

          {/* ── Явный выбор колонок для шаблона ── */}
          <div className="border border-gray-100 rounded-lg">
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
              <span className="text-xs font-medium text-gray-600">
                Колонки шаблона ({selectedCols.length})
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={selectAll}
                  className="text-[11px] text-blue-600 hover:underline"
                >
                  Все
                </button>
                <button
                  type="button"
                  onClick={selectNone}
                  className="text-[11px] text-gray-400 hover:underline"
                >
                  Ничего
                </button>
              </div>
            </div>
            <div className="max-h-44 overflow-y-auto p-1">
              {orderedColumns.map((col) => {
                const checked = selectedCols.includes(col.id)
                return (
                  <label
                    key={col.id}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm
                      ${col.fixed ? "opacity-50" : "hover:bg-gray-50 cursor-pointer"}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={col.fixed}
                      onChange={() => toggleCol(col.id, col.fixed)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-40 w-3.5 h-3.5"
                    />
                    <span className="text-gray-700">{col.label}</span>
                    {col.fixed && <span className="text-[10px] text-gray-300 ml-auto">фикс.</span>}
                  </label>
                )
              })}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              onClick={cancelEdit}
              className="px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50 rounded-lg"
            >
              Отмена
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? "Сохранение..." : "Сохранить"}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function pluralizeColumns(n) {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return "колонка"
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return "колонки"
  return "колонок"
}

export default ColumnTemplatesPanel
